import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SubscriptionContext = createContext(null);

// Dynamic Razorpay script loader
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.getElementById("razorpay-checkout-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-checkout-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [paywall, setPaywall] = useState({ isOpen: false, feature: "" });

  const loadSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/billing/subscription");
      setSubscription(data);
    } catch {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadPlans = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/plans");
      setPlans(data.plans || []);
    } catch {
      setPlans([]);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
    loadPlans();
  }, [loadSubscription, loadPlans]);

  const openPaywall = (_feature = "this feature") => {
    navigate("/pricing");
  };

  const closePaywall = () => {
    setPaywall({ isOpen: false, feature: "" });
  };

  const validateCoupon = async (couponCode, planId = null) => {
    if (!couponCode || !couponCode.trim()) {
      throw new Error("Please enter a coupon code.");
    }
    try {
      const payload = { coupon_code: couponCode.trim() };
      if (planId) {
        payload.plan_id = planId;
      }
      const { data } = await api.post("/billing/validate-coupon", payload);
      return data;
    } catch (err) {
      throw new Error(apiErrorMessage(err) || "Failed to validate coupon.");
    }
  };

  const openCheckout = async (planId, billingCycle = null, couponCode = null) => {
    if (!user) {
      toast.error("Please log in to purchase a plan.");
      return false;
    }

    setLoadingPlanId(planId);
    setCheckoutBusy(true);
    try {
      // 1. Ensure payment script is loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        toast.error("Could not load payment gateway. Please check your connection and try again.");
        setLoadingPlanId(null);
        setCheckoutBusy(false);
        return false;
      }

      // 2. Create order on backend (optionally with coupon)
      const orderPayload = {
        plan_id: planId,
        billing_cycle: billingCycle,
      };
      if (couponCode && couponCode.trim()) {
        orderPayload.coupon_code = couponCode.trim();
      }

      const { data: orderData } = await api.post("/billing/create-order", orderPayload);

      // 3. Launch checkout modal
      return new Promise((resolve) => {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "Virality",
          description: `${orderData.plan?.name || "Subscription"} Access`,
          order_id: orderData.order_id,
          handler: async function (response) {
            try {
              toast.loading("Verifying payment…", { id: "verify-loader" });
              const verifyPayload = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan_id: orderData.plan.id,
                billing_cycle: orderData.billing_cycle,
              };
              if (couponCode && couponCode.trim()) {
                verifyPayload.coupon_code = couponCode.trim();
              }

              const { data: verifyData } = await api.post("/billing/verify-payment", verifyPayload);

              toast.dismiss("verify-loader");
              toast.success(verifyData.message || "Subscription activated!");
              await loadSubscription();
              closePaywall();
              resolve(true);
            } catch (err) {
              toast.dismiss("verify-loader");
              toast.error(apiErrorMessage(err) || "Payment verification failed.");
              resolve(false);
            }
          },
          prefill: {
            name: user?.name || "",
            email: user?.email || "",
          },
          theme: {
            color: "#E8522A",
          },
          modal: {
            ondismiss: function () {
              setLoadingPlanId(null);
              setCheckoutBusy(false);
              resolve(false);
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", function (failResponse) {
          toast.error(failResponse.error?.description || "Payment failed. Please try again.");
          setLoadingPlanId(null);
          setCheckoutBusy(false);
          resolve(false);
        });

        // Open checkout and dismiss the button loader once the gateway modal renders
        rzp.open();
        setLoadingPlanId(null);
      });
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed to initialize checkout.");
      setLoadingPlanId(null);
      setCheckoutBusy(false);
      return false;
    }
  };

  const hasActiveSubscription = Boolean(subscription?.active);
  const isTrial = Boolean(subscription?.is_trial);
  const isAdmin = Boolean(subscription?.is_admin);

  const getQuotaLeft = (quotaType) => {
    if (!subscription?.active) return 0;
    if (subscription?.is_admin) return 999999;
    const limit = subscription?.quotas?.[quotaType] || 0;
    const used = subscription?.usage?.[quotaType] || 0;
    return Math.max(0, limit - used);
  };

  const isQuotaExhausted = (quotaType) => {
    if (!subscription?.active) return true;
    if (subscription?.is_admin) return false;
    return getQuotaLeft(quotaType) <= 0;
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        plans,
        loading,
        checkoutBusy,
        loadingPlanId,
        hasActiveSubscription,
        isTrial,
        isAdmin,
        paywall,
        openPaywall,
        closePaywall,
        openCheckout,
        validateCoupon,
        refreshSubscription: loadSubscription,
        getQuotaLeft,
        isQuotaExhausted,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
